/**
 * InspiredBy — inline attribution block on ContentPage.
 *
 * Renders at the END of a chain (after Modern Pattern step). Shows:
 *   "Inspired by Mrs. Rivera, 6th-grade history, El Paso ISD"
 *   "Lesson submitted via TSOGN Lessons Contest. Credibility 12 at publish."
 *
 * Methodology: this is not advertising. It's a verifiable receipt — the
 * teacher's lesson is on the platform because they trusted us with it.
 */
import { useEffect, useState } from 'react';
import { GraduationCap, ExternalLink } from 'lucide-react';
import api from '../../lib/api';

export default function InspiredBy({ chainId }) {
  const [attributions, setAttributions] = useState(null);

  useEffect(() => {
    if (!chainId) return;
    (async () => {
      try {
        const r = await api.get(`/chains/${chainId}/attributions`);
        setAttributions(r.data?.attributions || []);
      } catch {
        setAttributions([]);
      }
    })();
  }, [chainId]);

  if (!attributions || attributions.length === 0) return null;

  return (
    <div
      className="my-12 max-w-3xl mx-auto"
      data-testid="inspired-by-block"
    >
      <div className="border-t border-[#D4A84A]/20 pt-8">
        <div className="font-hud text-[10px] md:text-xs text-[#D4A84A] tracking-widest uppercase mb-3 flex items-center gap-2">
          <GraduationCap size={13} />
          {attributions.length === 1 ? 'Inspired by' : `Inspired by ${attributions.length} teachers`}
        </div>
        <ul className="space-y-3">
          {attributions.map((a) => (
            <li
              key={a.lesson_submission_id}
              className="flex items-start gap-3"
              data-testid={`inspired-by-${a.lesson_submission_id}`}
            >
              <div
                className="w-9 h-9 rounded-full border border-[#D4A84A]/30 flex items-center justify-center flex-shrink-0 bg-[#D4A84A]/5 mt-1"
              >
                <span className="font-historical text-sm text-[#D4A84A]">
                  {(a.teacher_name || '?').slice(0, 1).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <a
                  href={`/teachers/${a.teacher_id}`}
                  className="font-historical text-base md:text-lg text-white hover:text-[#D4A84A] transition-colors"
                  data-testid={`inspired-by-name-${a.lesson_submission_id}`}
                >
                  {a.teacher_name || 'TSOGN Teacher'}
                </a>
                <div className="font-mono text-[11px] md:text-xs text-white/65 leading-relaxed">
                  {a.grade_band} · {a.subject_area}
                  {a.school_label ? ` · ${a.school_label}` : ''}
                </div>
                <div className="font-mono text-[10px] md:text-[11px] text-white/45 mt-0.5 italic">
                  Lesson "{a.lesson_title}" · Credibility {a.credibility_score_at_publish} at publish · Submitted via the TSOGN Lessons Contest.
                </div>
              </div>
            </li>
          ))}
        </ul>
        <p className="font-mono text-[10px] text-white/35 mt-4 leading-relaxed">
          Teachers contributed lessons. We walked them through the methodology with sources, contradictions, and primary citations. Their names stay on the work.
        </p>
      </div>
    </div>
  );
}
